import React, {useCallback, useEffect, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";

import {calculateRemSize, m, onDesktop, p, prettyScroll} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";

import {FavouritesApplication} from "../../../favorite/components/FavouritesApplication/FavouritesApplication";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {useMyOffersFavourites} from "../../../my_offers/hooks/use_my_offers_favourites";
import {InquiryButtonTypeName} from "../../../offer/detail/constants/inquiry_button_type";
import {PropertyListItem} from "../../../property/components/PropertyListItem";
import {isPropertyArchived} from "../../../property/utils/is_property_archived";
import {gtmEventFormOpen} from "../../../tracking/google_tag_manager/gtm_event_form_open";
import {IAskForAllModalViewData} from "./ask_for_all_modal_types";

interface IProps {
    setModalView: (viewData: IAskForAllModalViewData) => void;
}

export const AskForAllModalListWithForm = (props: IProps) => {
    const {setModalView} = props;
    const {favouriteProperties} = useMyOffersFavourites();
    const [hasFog, setHasFog] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const activeProperties = favouriteProperties.filter((property) => !isPropertyArchived(property));

    const handleFogVisibility = useCallback(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            const scrolledToBottom = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight;

            if (scrolledToBottom) {
                setHasFog(false);
            }

            if (!scrolledToBottom) {
                setHasFog(true);
            }
        }
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        gtmEventFormOpen(InquiryButtonTypeName.ASK_FOR_ALL, GTMEventApplicationSourceSection.COMPARISON_TOOL, "", "");

        if (scrollContainer) {
            const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;

            if (hasScroll) {
                setHasFog(true);
            }

            scrollContainer.addEventListener("scroll", handleFogVisibility);

            return () => {
                scrollContainer.removeEventListener("scroll", handleFogVisibility);
            };
        }
    }, []);

    return (
        <div css={modalContentWrap}>
            <div css={applicationWrap}>
                <Text variant="headline_4" as="h4" css={mobileFormTitle}>
                    Zapytaj o porównywane oferty
                </Text>
                <FavouritesApplication
                    storeHash="comparison_tool_ask_for_all_application"
                    onSubmitSuccess={(_, sentOffers, rejectedOffers, sentProperties, rejectedProperties) => {
                        if (rejectedOffers.length === 0 && rejectedProperties.length === 0) {
                            setModalView({
                                type: "justConfirm"
                            });
                        }

                        if (rejectedOffers.length > 0 || rejectedProperties.length > 0) {
                            setModalView({
                                type: "informAboutRejected",
                                sentOffers,
                                rejectedOffers,
                                sentProperties,
                                rejectedProperties
                            });
                        }
                    }}
                />
            </div>
            <div css={listWithFog}>
                <Text variant="headline_4" as="h4" css={desktopFormTitle}>
                    Zapytaj o porównywane oferty
                </Text>
                <div css={listWrap(hasFog)}>
                    <div css={[scrollWrap, prettyScroll()]} ref={scrollContainerRef}>
                        <div css={listItems}>
                            {activeProperties.map((property) => (
                                <PropertyListItem
                                    key={`afamp${property.id}`}
                                    id={property.id}
                                    offerType={property.offer.type}
                                    offerName={property.offer.name}
                                    offerVendorName={property.offer.vendor.name}
                                    offerAddress={property.offer.address}
                                    offerPrivacyPolicyUrl={property.offer.configuration.privacy_policy_url}
                                    offerPrivacyDataProcessorName={property.offer.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={property.offer.configuration.personal_data_processor_url}
                                    rooms={property.rooms}
                                    area={property.area}
                                    imageSrc={property.offer.main_image.m_img_350x230}
                                    constructionDateRange={property.offer.construction_date_range}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/*
    Styles
 */
const modalContentWrap = (theme: Theme) => css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.sm}) {
        flex-direction: row;
    }
`;

const applicationWrap = (theme: Theme) => css`
    order: 0;
    background: ${theme.colors.background["100"]};
    ${p(2, 1.5, 4, 1.5)};

    @media (min-width: ${theme.breakpoints.sm}) {
        flex: 1 0 374px;
        order: 1;
        overflow: hidden;
        ${p(12, 2, 4, 2)}
    }
`;

const mobileFormTitle = (theme: Theme) => css`
    ${p(0, 10, 0, 1)};
    ${m(0, 0, 3, 0)};

    @media (min-width: ${theme.breakpoints.sm}) {
        display: none;
    }
`;

const desktopFormTitle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.sm}) {
        display: block;
        ${m(0, 0, 5, 0)}
    }
`;

const listWithFog = (theme: Theme) => css`
    order: 1;
    display: flex;
    flex-direction: column;
    ${p(2, 1.5, 4, 1.5)};
    position: relative;

    @media (min-width: ${theme.breakpoints.sm}) {
        flex: 1 1 auto;
        order: 0;
        ${p(3, 2, 3, 5)}
    }
`;

const listWrap = (hasFog: boolean) => () => css`
    position: relative;

    &::after {
        display: ${hasFog ? "block" : "none"};
        content: "";
        height: 70px;
        position: absolute;
        bottom: 0;
        left: 0;
        background: linear-gradient(360deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        width: 100%;
        z-index: 5;
    }
`;

const scrollWrap = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        max-height: 460px;
    }

    ${onDesktop(css`
        max-height: 490px;
    `)}
`;

const listItems = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    row-gap: ${calculateRemSize(1.5)};

    @media (min-width: ${theme.breakpoints.sm}) {
        row-gap: ${calculateRemSize(2)};
        ${p(0, 2, 0, 0)}
    }
`;
import React, {useCallback, useEffect, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";

import {calculateRemSize, m, onDesktop, p, prettyScroll} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";

import {FavouritesApplication} from "../../../favorite/components/FavouritesApplication/FavouritesApplication";
import {GTMEventApplicationSourceSection} from "../../../lead/types/GTMEvenApplicationSourceSection";
import {useMyOffersFavourites} from "../../../my_offers/hooks/use_my_offers_favourites";
import {InquiryButtonTypeName} from "../../../offer/detail/constants/inquiry_button_type";
import {PropertyListItem} from "../../../property/components/PropertyListItem";
import {isPropertyArchived} from "../../../property/utils/is_property_archived";
import {gtmEventFormOpen} from "../../../tracking/google_tag_manager/gtm_event_form_open";
import {IAskForAllModalViewData} from "./ask_for_all_modal_types";

interface IProps {
    setModalView: (viewData: IAskForAllModalViewData) => void;
}

export const AskForAllModalListWithForm = (props: IProps) => {
    const {setModalView} = props;
    const {favouriteProperties} = useMyOffersFavourites();
    const [hasFog, setHasFog] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const activeProperties = favouriteProperties.filter((property) => !isPropertyArchived(property));

    const handleFogVisibility = useCallback(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            const scrolledToBottom = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight;

            if (scrolledToBottom) {
                setHasFog(false);
            }

            if (!scrolledToBottom) {
                setHasFog(true);
            }
        }
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        gtmEventFormOpen(InquiryButtonTypeName.ASK_FOR_ALL, GTMEventApplicationSourceSection.COMPARISON_TOOL, "", "");

        if (scrollContainer) {
            const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;

            if (hasScroll) {
                setHasFog(true);
            }

            scrollContainer.addEventListener("scroll", handleFogVisibility);

            return () => {
                scrollContainer.removeEventListener("scroll", handleFogVisibility);
            };
        }
    }, []);

    return (
        <div css={modalContentWrap}>
            <div css={applicationWrap}>
                <Text variant="headline_4" as="h4" css={mobileFormTitle}>
                    Zapytaj o porównywane oferty
                </Text>
                <FavouritesApplication
                    storeHash="comparison_tool_ask_for_all_application"
                    onSubmitSuccess={(_, sentOffers, rejectedOffers, sentProperties, rejectedProperties) => {
                        if (rejectedOffers.length === 0 && rejectedProperties.length === 0) {
                            setModalView({
                                type: "justConfirm"
                            });
                        }

                        if (rejectedOffers.length > 0 || rejectedProperties.length > 0) {
                            setModalView({
                                type: "informAboutRejected",
                                sentOffers,
                                rejectedOffers,
                                sentProperties,
                                rejectedProperties
                            });
                        }
                    }}
                />
            </div>
            <div css={listWithFog}>
                <Text variant="headline_4" as="h4" css={desktopFormTitle}>
                    Zapytaj o porównywane oferty
                </Text>
                <div css={listWrap(hasFog)}>
                    <div css={[scrollWrap, prettyScroll()]} ref={scrollContainerRef}>
                        <div css={listItems}>
                            {activeProperties.map((property) => (
                                <PropertyListItem
                                    key={`afamp${property.id}`}
                                    id={property.id}
                                    offerType={property.offer.type}
                                    offerName={property.offer.name}
                                    offerVendorName={property.offer.vendor.name}
                                    offerAddress={property.offer.address}
                                    offerPrivacyPolicyUrl={property.offer.configuration.privacy_policy_url}
                                    offerPrivacyDataProcessorName={property.offer.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={property.offer.configuration.personal_data_processor_url}
                                    rooms={property.rooms}
                                    area={property.area}
                                    imageSrc={property.offer.main_image.m_img_350x230}
                                    constructionDateRange={property.offer.construction_date_range}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/*
    Styles
 */
const modalContentWrap = (theme: Theme) => css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${theme.breakpoints.sm}) {
        flex-direction: row;
    }
`;

const applicationWrap = (theme: Theme) => css`
    order: 0;
    background: ${theme.colors.background["100"]};
    ${p(2, 1.5, 4, 1.5)};

    @media (min-width: ${theme.breakpoints.sm}) {
        flex: 1 0 374px;
        order: 1;
        overflow: hidden;
        ${p(12, 2, 4, 2)}
    }
`;

const mobileFormTitle = (theme: Theme) => css`
    ${p(0, 10, 0, 1)};
    ${m(0, 0, 3, 0)};

    @media (min-width: ${theme.breakpoints.sm}) {
        display: none;
    }
`;

const desktopFormTitle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.sm}) {
        display: block;
        ${m(0, 0, 5, 0)}
    }
`;

const listWithFog = (theme: Theme) => css`
    order: 1;
    display: flex;
    flex-direction: column;
    ${p(2, 1.5, 4, 1.5)};
    position: relative;

    @media (min-width: ${theme.breakpoints.sm}) {
        flex: 1 1 auto;
        order: 0;
        ${p(3, 2, 3, 5)}
    }
`;

const listWrap = (hasFog: boolean) => () => css`
    position: relative;

    &::after {
        display: ${hasFog ? "block" : "none"};
        content: "";
        height: 70px;
        position: absolute;
        bottom: 0;
        left: 0;
        background: linear-gradient(360deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        width: 100%;
        z-index: 5;
    }
`;

const scrollWrap = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        max-height: 460px;
    }

    ${onDesktop(css`
        max-height: 490px;
    `)}
`;

const listItems = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    row-gap: ${calculateRemSize(1.5)};

    @media (min-width: ${theme.breakpoints.sm}) {
        row-gap: ${calculateRemSize(2)};
        ${p(0, 2, 0, 0)}
    }
`;
